import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { QrCode, Copy, Users, Shield, Clock, CheckCircle, Key, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { AccessLevelInfo } from './AccessLevelInfo';
import { RestrictionsConfig } from './RestrictionsConfig';

interface GameCodeSystemProps {
  gameId: string;
  gameType: 'regular' | 'venue';
  gameCode?: string;
  accessLevel: 'open' | 'restricted' | 'closed';
  onAccessLevelChange?: (level: 'open' | 'restricted' | 'closed') => void;
}

export const GameCodeSystem: React.FC<GameCodeSystemProps> = ({
  gameId,
  gameType,
  gameCode,
  accessLevel,
  onAccessLevelChange
}) => {
  const [codeUsage, setCodeUsage] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showRestrictions, setShowRestrictions] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (gameCode) {
      loadCodeUsage();
    }
  }, [gameCode]);

  const loadCodeUsage = async () => {
    try {
      setLoading(true);
      
      // Load code usage statistics
      const { data: participants, error } = await supabase
        .from('game_participants')
        .select(`
          id,
          player_id,
          team_side,
          participation_status,
          created_at,
          profiles (
            username,
            first_name,
            last_name
          )
        `)
        .eq('game_id', gameId);

      if (error) throw error;

      setCodeUsage(participants || []);
    } catch (error) {
      console.error('Error loading code usage:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyGameCode = () => {
    if (gameCode) {
      navigator.clipboard.writeText(gameCode);
      toast.success('Game code copied to clipboard');
    }
  };

  const generateQRCode = () => {
    // Generate QR code URL for the game
    const gameUrl = `${window.location.origin}/games/${gameId}/unified-form?code=${gameCode}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(gameUrl)}`;
  };

  const getAccessLevelIcon = (level: string) => {
    switch (level) {
      case 'open':
        return <Users className="h-4 w-4 text-green-500" />;
      case 'restricted':
        return <Shield className="h-4 w-4 text-yellow-500" />;
      case 'closed':
        return <Key className="h-4 w-4 text-red-500" />;
      default:
        return <Users className="h-4 w-4" />;
    }
  };

  const getAccessLevelDescription = (level: string) => {
    switch (level) {
      case 'open':
        return 'Anyone with the code can join immediately - no approval needed';
      case 'restricted':
        return 'Join requests require approval from coaches/team owners';
      case 'closed':
        return 'Game roster is locked - no new participants allowed';
      default:
        return 'Unknown access level';
    }
  };

  const updateAccessLevel = async (newLevel: 'open' | 'restricted' | 'closed') => {
    try {
      const table = gameType === 'venue' ? 'venue_games' : 'games';
      const { error } = await supabase
        .from(table)
        .update({ 
          game_code_access_level: newLevel,
          updated_at: new Date().toISOString()
        })
        .eq('id', gameId);

      if (error) throw error;

      onAccessLevelChange?.(newLevel);
      toast.success(`Access level updated to ${newLevel}`);
    } catch (error) {
      console.error('Error updating access level:', error);
      toast.error('Failed to update access level');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <QrCode className="h-5 w-5" />
          Game Code System
        </CardTitle>
        <CardDescription>
          Share this code for players to join the game
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Game Code Display */}
        {gameCode && (
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-3xl font-bold tracking-wider text-primary mb-2">
                {gameCode}
              </div>
              <div className="flex justify-center gap-2">
                <Button variant="outline" size="sm" onClick={copyGameCode}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Code
                </Button>
                <Dialog open={showQR} onOpenChange={setShowQR}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <QrCode className="h-4 w-4 mr-2" />
                      Show QR
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>QR Code for Game {gameCode}</DialogTitle>
                      <DialogDescription>
                        Players can scan this QR code to join the game
                      </DialogDescription>
                    </DialogHeader>
                    <div className="text-center py-4">
                      <img 
                        src={generateQRCode()} 
                        alt="Game QR Code" 
                        className="mx-auto"
                      />
                      <div className="text-sm text-muted-foreground mt-2">
                        Code: {gameCode}
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>
        )}

        <Separator />

        {/* Access Level Control */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">Access Control</h4>
            <Collapsible open={showHelp} onOpenChange={setShowHelp}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="text-muted-foreground">
                  <HelpCircle className="h-4 w-4 mr-1" />
                  How it Works
                  {showHelp ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <div className="p-3 bg-muted/50 rounded-lg text-sm space-y-2">
                  <div><strong>Open:</strong> Players join instantly when they enter the game code. Best for casual games.</div>
                  <div><strong>Restricted:</strong> Players submit join requests that require approval. Configure detailed restrictions below.</div>
                  <div><strong>Closed:</strong> No new players can join. Use when roster is finalized.</div>
                  <p className="text-xs text-muted-foreground mt-2">
                    💡 Tip: Use the info icons (ℹ️) next to each option for detailed explanations.
                  </p>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div className="flex items-center gap-3">
              {getAccessLevelIcon(accessLevel)}
              <div>
                <div className="font-medium capitalize flex items-center gap-2">
                  {accessLevel} Access
                  <AccessLevelInfo level={accessLevel} />
                </div>
                <div className="text-sm text-muted-foreground">
                  {getAccessLevelDescription(accessLevel)}
                </div>
              </div>
            </div>
            <Badge variant={
              accessLevel === 'open' ? 'default' :
              accessLevel === 'restricted' ? 'secondary' : 'destructive'
            }>
              {accessLevel.toUpperCase()}
            </Badge>
          </div>

          {/* Access Level Controls */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="relative">
              <Button
                variant={accessLevel === 'open' ? 'default' : 'outline'}
                size="sm"
                onClick={() => updateAccessLevel('open')}
                disabled={accessLevel === 'open'}
                className={`w-full justify-start transition-all duration-200 ${
                  accessLevel === 'open' 
                    ? 'bg-[hsl(var(--section-completed))] text-white border-[hsl(var(--section-completed))] shadow-md' 
                    : 'hover:bg-[hsl(var(--section-completed))]/10 hover:border-[hsl(var(--section-completed))]/50'
                }`}
              >
                <Users className="h-4 w-4 mr-2" />
                Open
                {accessLevel === 'open' && <CheckCircle className="h-4 w-4 ml-auto" />}
              </Button>
              <div className="absolute -top-1 -right-1">
                <AccessLevelInfo level="open" />
              </div>
            </div>
            <div className="relative">
              <Button
                variant={accessLevel === 'restricted' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  updateAccessLevel('restricted');
                  setShowRestrictions(true);
                }}
                disabled={accessLevel === 'restricted'}
                className={`w-full justify-start transition-all duration-200 ${
                  accessLevel === 'restricted' 
                    ? 'bg-[hsl(var(--action-required))] text-black border-[hsl(var(--action-required))] shadow-md' 
                    : 'hover:bg-[hsl(var(--action-required))]/10 hover:border-[hsl(var(--action-required))]/50'
                }`}
              >
                <Shield className="h-4 w-4 mr-2" />
                Restricted
                {accessLevel === 'restricted' && <CheckCircle className="h-4 w-4 ml-auto" />}
              </Button>
              <div className="absolute -top-1 -right-1">
                <AccessLevelInfo level="restricted" />
              </div>
            </div>
            <div className="relative">
              <Button
                variant={accessLevel === 'closed' ? 'destructive' : 'outline'}
                size="sm"
                onClick={() => updateAccessLevel('closed')}
                disabled={accessLevel === 'closed'}
                className={`w-full justify-start transition-all duration-200 ${
                  accessLevel === 'closed' 
                    ? 'bg-[hsl(var(--section-blocked))] text-white border-[hsl(var(--section-blocked))] shadow-md' 
                    : 'hover:bg-[hsl(var(--section-blocked))]/10 hover:border-[hsl(var(--section-blocked))]/50'
                }`}
              >
                <Key className="h-4 w-4 mr-2" />
                Closed
                {accessLevel === 'closed' && <CheckCircle className="h-4 w-4 ml-auto" />}
              </Button>
              <div className="absolute -top-1 -right-1">
                <AccessLevelInfo level="closed" />
              </div>
            </div>
          </div>

          {/* Restrictions Configuration */}
          {accessLevel === 'restricted' && (
            <Collapsible open={showRestrictions} onOpenChange={setShowRestrictions}>
              <CollapsibleTrigger asChild>
                <Button variant="outline" size="sm" className="w-full">
                  Configure Restrictions
                  {showRestrictions ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <RestrictionsConfig 
                  gameId={gameId}
                  gameType={gameType}
                  isVisible={showRestrictions}
                />
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>

        <Separator />

        {/* Quick Start Guide */}
        <div className="bg-muted/30 p-4 rounded-lg">
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
            🚀 Quick Start Guide
          </h4>
          <div className="text-sm space-y-1 text-muted-foreground">
            <p><strong>Step 1:</strong> Choose your access level above</p>
            <p><strong>Step 2:</strong> Share the game code with participants</p>
            <p><strong>Step 3:</strong> Monitor join requests and approvals below</p>
            {accessLevel === 'restricted' && (
              <p><strong>Tip:</strong> Configure detailed restrictions to control who can join</p>
            )}
          </div>
        </div>

        <Separator />

        {/* Code Usage Statistics */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold">Code Usage</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold">{codeUsage.length}</div>
              <div className="text-sm text-muted-foreground">Total Joins</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">
                {codeUsage.filter(u => u.participation_status === 'confirmed').length}
              </div>
              <div className="text-sm text-muted-foreground">Confirmed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">
                {codeUsage.filter(u => u.participation_status === 'requested').length}
              </div>
              <div className="text-sm text-muted-foreground">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">
                {codeUsage.filter(u => u.team_side === 'home').length}
              </div>
              <div className="text-sm text-muted-foreground">Home Team</div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold">Recent Activity</h4>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {codeUsage.slice(0, 5).map((usage) => (
              <div key={usage.id} className="flex items-center justify-between p-2 border rounded text-sm">
                <div className="flex items-center gap-2">
                  <div className="font-medium">
                    {(usage.profiles as any)?.username || 
                     `${(usage.profiles as any)?.first_name} ${(usage.profiles as any)?.last_name}` || 
                     'Unknown Player'}
                  </div>
                  <Badge variant={usage.team_side === 'home' ? 'default' : 'secondary'}>
                    {usage.team_side}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  {usage.participation_status === 'confirmed' ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <Clock className="h-4 w-4 text-yellow-500" />
                  )}
                  <span className="text-xs text-muted-foreground">
                    {new Date(usage.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
            {codeUsage.length === 0 && (
              <div className="text-center py-4 text-muted-foreground">
                No one has joined using the code yet
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};